import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../core/src/helpers/browser/hoverHelpers';
import {
  buildInteractionIdProps,
  InteractionComponentProps,
} from '../../../../../core/src/helpers/interaction/interactionHelpers';
import {InteractionTypesEnum} from '../../../../../core/src/helpers/interaction/interactionTypes';
import {FrontEvent, RendererOf} from '../../../../../core/src/helpers/react/reactHelpers';
import {DataEventActionsEnum} from '../../../../../core/src/models/tracking/dataEventActions';
import {isDataEventObjectName} from '../../../../../core/src/models/tracking/dataEventObjectName';
import {useDataEventTracking} from '../../../../../core/src/services/tracking/dataEventTrackingContext';
import {useLegacyInteractionContext} from '../../../interactionTracking/interactionContext';
import {useInteractionTracking} from '../../../interactionTracking/interactionTrackingContext';
import {
  InteractiveListItem,
  InteractiveListItemChildrenProps,
  InteractiveListItemPublicProps,
} from '../../interactiveList/interactiveListItem';
import {RepositionPlacement} from '../../reposition/repositionHelpers';
import {TooltipCoordinator} from '../../tooltips/tooltipCoordinator';
import {TooltipCoordinatorRenderProps} from '../../tooltips/tooltipCoordinatorTypes';
import {DropdownItemCheckmark} from './dropdownItemCheckmark';

/*
 * Constants.
 */

export const DROPDOWN_ITEM_DISABLED_OPACITY = 0.5;

/*
 * Props.
 */

export interface DropdownItemRightContentProps {
  isHighlighted?: boolean;
}

export interface DropdownItemProps extends InteractiveListItemPublicProps, InteractionComponentProps {
  checkmarkLabel?: ReactNode;
  isChecked?: boolean;
  isDisabled?: boolean;
  shouldHideCheckmark?: boolean;
  renderRightContent?: RendererOf<DropdownItemRightContentProps>;
  renderTooltip?: RendererOf<TooltipCoordinatorRenderProps>;
  plainTooltip?: ReactNode;
  tooltipPersistent?: boolean;
  tooltipPlacement?: RepositionPlacement;
  tooltipMaxWidth?: number;
  tooltipBorderRadius?: number;
  className?: string;
  shouldReserveRightIcon?: boolean;
  isNotInteractive?: boolean;
  isCheckbox?: boolean;
  onCheckboxChange?: (isChecked: boolean) => void;
}

/*
 * Style.
 */

interface StyleProps {
  $shouldReserveRightIcon?: boolean;
}

export const dropdownItemHoverBackgroundColor = css`
  background-color: ${(p) => p.theme.alphas.gray10};

  ${(p) =>
    p.theme.hasDropdownRedesign &&
    css`
      background-color: ${p.theme.background.surface.tertiaryHover};
    `}
`;

export const StyledDropdownItemDiv = styled.div<StyleProps>`
  display: grid;
  grid-template-columns: auto auto 1fr auto ${(p) => (p.$shouldReserveRightIcon ? '24px' : 'auto')};
  grid-template-areas: 'left-icon content secondary-content badges right-icon';
  justify-items: start;

  padding: ${(p) => (p.theme.hasDropdownRedesign ? '6px 8px' : '5px 12px')};

  &:hover,
  &:focus-within,
  &:active,
  &[aria-selected='true'] {
    ${dropdownItemHoverBackgroundColor}
  }

  &:disabled,
  &[aria-disabled='true'] {
    opacity: ${DROPDOWN_ITEM_DISABLED_OPACITY};
    background-color: ${(p) => p.theme.alphas.transparent};
  }

  ${(p) =>
    p.theme.hasDropdownRedesign &&
    css`
      border-radius: 4px;

      background-color: ${p.theme.alphas.transparent};
      color: ${p.theme.textStyles.default};
    `}
`;

/*
 * Component.
 */

export const DropdownItem: FC<PropsWithChildren<DropdownItemProps>> = (props) => {
  const interactionContext = useLegacyInteractionContext();
  const {trackComponentInteraction} = useInteractionTracking();
  const {trackDataEvent} = useDataEventTracking();

  const onSelect = (event: FrontEvent) => {
    if (props.isDisabled) {
      event.preventDefault();
      return;
    }

    if (props.onSelect) {
      props.onSelect(event);
    }

    if (props.interactionId && isDataEventObjectName(props.interactionId)) {
      trackDataEvent(DataEventActionsEnum.SELECT, props.interactionId, props.interactionData);
    } else {
      trackComponentInteraction(InteractionTypesEnum.CLICK, interactionContext, props, event);
    }
  };

  function renderDropdownItemDiv(
    itemProps: InteractiveListItemChildrenProps['itemProps'],
    isHighlighted: boolean,
  ) {
    return (
      <StyledDropdownItemDiv
        {...itemProps}
        className={buildHoverParentClassName(isHighlighted, props.className)}
        aria-disabled={props.isDisabled}
        aria-selected={isHighlighted}
        $shouldReserveRightIcon={props.shouldReserveRightIcon}
        {...buildInteractionIdProps(props.interactionId)}
      >
        {props.children}
        {renderRightIcon(props, isHighlighted)}
      </StyledDropdownItemDiv>
    );
  }

  if (props.isNotInteractive) {
    return <>{maybeWrapInTooltip(props, renderDropdownItemDiv(null, false))}</>;
  }

  return (
    <InteractiveListItem {...props} onSelect={onSelect}>
      {({itemProps, isHighlighted}) =>
        maybeWrapInTooltip(props, renderDropdownItemDiv(itemProps, isHighlighted))
      }
    </InteractiveListItem>
  );
};

function maybeWrapInTooltip(props: DropdownItemProps, children: ReactNode) {
  if (!props.renderTooltip && !props.plainTooltip) {
    return children;
  }

  return (
    <TooltipCoordinator
      placement={props.tooltipPlacement}
      persistent={props.tooltipPersistent}
      render={props.renderTooltip}
      plainTooltip={props.plainTooltip}
      maxWidth={props.tooltipMaxWidth}
      borderRadius={props.tooltipBorderRadius}
    >
      {children}
    </TooltipCoordinator>
  );
}

function renderRightIcon(props: DropdownItemProps, isHighlighted: boolean) {
  // If we have a generic render prop, use it
  if (props.renderRightContent) {
    return props.renderRightContent({isHighlighted});
  }

  // If we have a checked value, we need to render a checkmark.
  if (props.isChecked !== undefined && !props.shouldHideCheckmark) {
    return (
      <DropdownItemCheckmark
        label={props.checkmarkLabel}
        isChecked={props.isChecked}
        isCheckbox={props.isCheckbox}
        isHovered={isHighlighted}
        onCheckboxChange={props.onCheckboxChange}
      />
    );
  }

  // Otherwise, nothing to render.
  return null;
}
